import * as React from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getJSON, postJSON } from '@/lib/api'
import { RenewalPipelineActionDialogs } from '@/pages/RenewalPipelineActionDialogs'
import type { PipelineAction, PipelineRow } from '@/pages/RenewalPipelinePage.types'
import { RenewalPipelineTableRow } from '@/pages/RenewalPipelineTableRow'
import { RenewalPipelineTimelineDialog } from '@/pages/RenewalPipelineTimelineDialog'
import { useRenewalDecisionDismissal } from '@/pages/useRenewalDecisionDismissal'
import {
  nextPipelineSort,
  pipelineDateHeading,
  PipelineSortHead,
  renewalPipelineEndpoint,
  RenewalPipelineToolbar,
  useDebouncedValue,
  type PipelineSort,
  type PipelineSortKey,
  type StageKey,
} from '@/pages/RenewalPipelineListControls'

interface RenewalPipelineResponse {
  renewal_pipeline: {
    as_of: string
    grace_days: number
    counts: Record<StageKey, number>
    stages: Record<StageKey, PipelineRow[]>
    filter_options: {
      levels: string[]
      autopay_states: string[]
    }
  }
}

const stageDefinitions: Array<{ key: StageKey; label: string }> = [
  { key: 'needs_decision', label: 'Needs decision' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'in_grace', label: 'In grace' },
  { key: 'lapsing_within_7_days', label: 'Lapsing ≤ 7 days' },
  { key: 'recently_lapsed', label: 'Recently lapsed' },
]

export function RenewalPipelinePage({
  currentAssociationDate,
  onOpenMember,
  onOpenOrganization,
}: {
  currentAssociationDate?: string
  onOpenMember: (personId: number) => void
  onOpenOrganization?: (organizationId: number) => void
}) {
  const queryClient = useQueryClient()
  const [stage, setStage] = React.useState<StageKey>('needs_decision')
  const [action, setAction] = React.useState<PipelineAction | null>(null)
  const [timelineRow, setTimelineRow] = React.useState<PipelineRow | null>(null)
  const [notice, setNotice] = React.useState<string | null>(null)
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [levels, setLevels] = React.useState<Set<string>>(() => new Set())
  const [autopayStates, setAutopayStates] = React.useState<Set<string>>(() => new Set())
  const [sort, setSort] = React.useState<PipelineSort | null>(null)
  const endpoint = renewalPipelineEndpoint({
    search: debouncedSearch,
    levels,
    autopayStates,
    stage,
    sort,
  })
  const pipelineQuery = useQuery({
    queryKey: ['renewal-pipeline', endpoint],
    queryFn: () => getJSON<RenewalPipelineResponse>(endpoint),
    placeholderData: keepPreviousData,
    retry: false,
  })
  const chargeNow = useMutation({
    mutationFn: (invoiceId: number) => postJSON(`/api/v1/invoices/${invoiceId}/charge_now`, {}),
    onSuccess: async () => {
      setNotice('Saved Card charge completed.')
      await queryClient.invalidateQueries({ queryKey: ['renewal-pipeline'] })
    },
    onError: () => setNotice('Saved Card charge could not be completed.'),
  })
  const dismissal = useRenewalDecisionDismissal(setNotice)
  const pipeline = pipelineQuery.data?.renewal_pipeline

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <h1 className="text-heading-sm font-semibold text-foreground">Renewal pipeline</h1>
      </header>

      {pipeline && (
        <StageTiles
          counts={pipeline.counts}
          selected={stage}
          onSelect={(nextStage) => {
            setStage(nextStage)
            setSort(null)
          }}
        />
      )}
      {notice && <Alert className="mt-4">{notice}</Alert>}
      <DecisionDismissalNotice dismissal={dismissal} clearNotice={() => setNotice(null)} />

      <RenewalPipelineToolbar
        search={search}
        onSearchChange={setSearch}
        levelOptions={pipeline?.filter_options.levels ?? []}
        levels={levels}
        onLevelsChange={setLevels}
        autopayOptions={pipeline?.filter_options.autopay_states ?? []}
        autopayStates={autopayStates}
        onAutopayStatesChange={setAutopayStates}
      />

      <div className="mt-4 overflow-hidden rounded-md border border-border bg-card">
        <PipelineTableState
          loading={pipelineQuery.isLoading}
          failed={pipelineQuery.isError}
          stage={stage}
          rows={pipeline?.stages[stage] ?? []}
          sort={sort}
          onSort={(key) => setSort(nextPipelineSort(sort, key))}
          chargingInvoiceId={chargeNow.isPending ? chargeNow.variables : undefined}
          onAction={setAction}
          onChargeNow={(invoiceId) => chargeNow.mutate(invoiceId)}
          dismissingMembershipId={
            dismissal.dismiss.isPending ? dismissal.dismiss.variables?.membership_id : undefined
          }
          onDismiss={(row) => {
            setNotice(null)
            dismissal.dismiss.mutate(row)
          }}
          onOpenTimeline={setTimelineRow}
          onOpenMember={onOpenMember}
          onOpenOrganization={onOpenOrganization}
        />
      </div>

      <RenewalPipelineActionDialogs
        action={action}
        currentAssociationDate={currentAssociationDate}
        onClose={() => setAction(null)}
      />
      <TimelineDialogState
        row={timelineRow}
        action={action}
        dismissedMembershipId={dismissal.dismissedMember?.membership_id}
        dismissing={dismissal.dismiss.isPending}
        onClose={() => setTimelineRow(null)}
        onDismiss={(row) => dismissal.dismiss.mutate(row)}
        onAction={setAction}
      />
    </div>
  )
}

function TimelineDialogState({
  row,
  action,
  dismissedMembershipId,
  dismissing,
  onClose,
  onDismiss,
  onAction,
}: {
  row: PipelineRow | null
  action: PipelineAction | null
  dismissedMembershipId?: number
  dismissing: boolean
  onClose: () => void
  onDismiss: (row: PipelineRow) => void
  onAction: (action: PipelineAction) => void
}) {
  const displayRow =
    row && dismissedMembershipId === row.membership_id ? { ...row, dismissible: false } : row
  return (
    <RenewalPipelineTimelineDialog
      row={displayRow}
      open={row !== null && action === null}
      dismissing={dismissing}
      onClose={onClose}
      onDismiss={onDismiss}
      onAction={onAction}
    />
  )
}

function DecisionDismissalNotice({
  dismissal,
  clearNotice,
}: {
  dismissal: ReturnType<typeof useRenewalDecisionDismissal>
  clearNotice: () => void
}) {
  if (!dismissal.dismissedMember) return null

  return (
    <Alert variant="warning" className="mt-4 flex items-center justify-between gap-3">
      <span>{dismissal.dismissedMember.name} removed from Needs decision.</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={dismissal.undo.isPending}
        onClick={() => {
          clearNotice()
          dismissal.undo.mutate(dismissal.dismissedMember!.membership_id)
        }}
      >
        {dismissal.undo.isPending ? 'Undoing…' : 'Undo'}
      </Button>
    </Alert>
  )
}

function StageTiles({
  counts,
  selected,
  onSelect,
}: {
  counts: Record<StageKey, number>
  selected: StageKey
  onSelect: (stage: StageKey) => void
}) {
  return (
    <Card>
      <CardContent className="grid grid-cols-2 divide-x divide-y divide-border p-0 lg:grid-cols-5 lg:divide-y-0">
        {stageDefinitions.map(({ key, label }) => {
          const count = counts[key]
          const isSelected = selected === key
          return (
            <button
              key={key}
              type="button"
              className={`flex min-h-20 flex-col items-start px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                isSelected ? 'border-b-2 border-b-primary' : ''
              }`}
              aria-label={`${label}, ${count} ${count === 1 ? 'member' : 'members'}`}
              aria-pressed={isSelected}
              onClick={() => onSelect(key)}
            >
              <span
                className={`text-caption uppercase tracking-wide ${
                  isSelected ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {label}
              </span>
              <span className="mt-1 text-heading-sm font-semibold tabular-nums text-foreground">
                {count}
              </span>
            </button>
          )
        })}
      </CardContent>
    </Card>
  )
}

function PipelineTableState({
  loading,
  failed,
  stage,
  rows,
  sort,
  onSort,
  chargingInvoiceId,
  onAction,
  onChargeNow,
  dismissingMembershipId,
  onDismiss,
  onOpenTimeline,
  onOpenMember,
  onOpenOrganization,
}: {
  loading: boolean
  failed: boolean
  stage: StageKey
  rows: PipelineRow[]
  sort: PipelineSort | null
  onSort: (key: PipelineSortKey) => void
  chargingInvoiceId?: number
  onAction: (action: PipelineAction) => void
  onChargeNow: (invoiceId: number) => void
  dismissingMembershipId?: number
  onDismiss: (row: PipelineRow) => void
  onOpenTimeline: (row: PipelineRow) => void
  onOpenMember: (personId: number) => void
  onOpenOrganization?: (organizationId: number) => void
}) {
  if (loading) return <EmptyState title="Loading Renewal Pipeline" className="min-h-52" />
  if (failed) {
    return (
      <EmptyState
        title="Renewal Pipeline unavailable"
        description="Refresh and try again."
        className="min-h-52"
      />
    )
  }
  if (rows.length === 0) {
    return (
      <EmptyState
        title={`No members in ${stageLabel(stage)}`}
        description="This live stage has no members right now."
        className="min-h-52"
      />
    )
  }

  return (
    <Table>
      <PipelineTableHeader stage={stage} sort={sort} onSort={onSort} />
      <TableBody>
        {rows.map((row) => (
          <RenewalPipelineTableRow
            key={row.membership_id}
            row={row}
            stage={stage}
            charging={chargingInvoiceId === row.invoice_id}
            dismissing={dismissingMembershipId === row.membership_id}
            onAction={onAction}
            onChargeNow={onChargeNow}
            onDismiss={onDismiss}
            onOpenTimeline={onOpenTimeline}
            onOpenMember={onOpenMember}
            onOpenOrganization={onOpenOrganization}
          />
        ))}
      </TableBody>
    </Table>
  )
}

function PipelineTableHeader({
  stage,
  sort,
  onSort,
}: {
  stage: StageKey
  sort: PipelineSort | null
  onSort: (key: PipelineSortKey) => void
}) {
  return (
    <TableHeader>
      <TableRow>
        <PipelineSortHead column="member" sort={sort} onSort={onSort}>
          Member
        </PipelineSortHead>
        <PipelineSortHead column="status" sort={sort} onSort={onSort}>
          Status
        </PipelineSortHead>
        {stage === 'needs_decision' ? (
          <>
            <TableHead>Why</TableHead>
            <PipelineSortHead column="open" sort={sort} onSort={onSort} className="text-right">
              Open
            </PipelineSortHead>
            <PipelineSortHead column="next" sort={sort} onSort={onSort}>
              Lapses
            </PipelineSortHead>
          </>
        ) : (
          <>
            <PipelineSortHead column="level" sort={sort} onSort={onSort}>
              Level
            </PipelineSortHead>
            <PipelineSortHead column="open" sort={sort} onSort={onSort} className="text-right">
              Open
            </PipelineSortHead>
            <PipelineSortHead column="due" sort={sort} onSort={onSort}>
              {pipelineDateHeading(stage)}
            </PipelineSortHead>
            <PipelineSortHead column="next" sort={sort} onSort={onSort}>
              Next
            </PipelineSortHead>
          </>
        )}
        {stage !== 'needs_decision' && (
          <PipelineSortHead column="autopay" sort={sort} onSort={onSort}>
            Autopay
          </PipelineSortHead>
        )}
        <TableHead className="w-12" aria-label="Row actions" />
      </TableRow>
    </TableHeader>
  )
}

function stageLabel(stage: StageKey) {
  return stageDefinitions.find((definition) => definition.key === stage)?.label ?? stage
}
